/**
 * The patient portal, V3.
 *
 * One file, three screens: sign in, set your password, and the dashboard.
 * Kept together because a patient sees exactly one of them at a time and the
 * whole surface is smaller than a single clinic page.
 *
 * Tone matters here in a way it does not on the clinic side. This is read by
 * someone looking at their own health results, often alone, sometimes worried.
 * So: no clinical jargon in the chrome, no empty states that read like an
 * error, and nothing that implies a result is being withheld from them.
 */

import { useEffect, useRef, useState } from 'react';
import {
  Link,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import {
  PortalProvider,
  portalRequest,
  usePortal,
  type PortalMe,
} from '../lib/portal';
import { getStripe, type StripeJs } from '../lib/stripe';
import {
  Button,
  Card,
  Disclosure,
  EmptyState,
  Field,
  LoadingRows,
  Pill,
  Spinner,
  inputClass,
} from '../components/ui';
import {
  Card as PhoneCard,
  CardHead,
  DISPLAY,
  PageTitle,
  PortalShell,
  TONE,
  formatPortalDate,
  usePortalQuery,
} from './portal/shell';
import { MovePill, PortalHome, ScoreRing } from './portal/Home';
import { ArrowLeft, ArrowRight } from './portal/icons';
import { PortalTwin } from './portal/Twin';
import { PortalPlan } from './portal/Plan';
import { PortalConversation, PortalMessages, PortalNewMessage } from './portal/Messages';
import { PortalProgress } from './portal/Progress';
import { PortalResources } from './portal/Resources';
import { PortalBodyScan } from './portal/BodyScan';
import { PortalDoseSteps } from './portal/DoseSteps';

/* ================================================================== *
 * Sign in
 * ================================================================== */

function PortalLogin() {
  const { login } = usePortal();
  const navigate = useNavigate();
  // The form's "Start my body scan" button opens /portal/scan signed out: come back to it after signing in.
  const location = useLocation();
  const back = location.pathname.startsWith('/portal/') && location.pathname !== '/portal/set-password' ? location.pathname + location.search : '/portal';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await login(email, password);
      navigate(back, { replace: true });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Sign-in failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen grid place-items-center px-4 py-10">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <h1 className="font-display text-2xl font-extrabold text-ink">Your health results</h1>
          <p className="mt-1.5 text-sm text-ink-muted">
            Sign in to see your reports and track your progress.
          </p>
        </div>

        <form onSubmit={onSubmit} className="glass-prominent mcos-enter rounded-lg2 p-6 space-y-4">
          <Field label="Email" required>
            <input
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              className={inputClass}
              placeholder="you@example.com"
            />
          </Field>

          <Field label="Password" required>
            <input
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              className={inputClass}
              placeholder="••••••••"
            />
          </Field>

          {error && (
            <p
              role="alert"
              className="text-sm text-red-600 bg-red-50/80 border border-red-200 rounded-control px-3 py-2"
            >
              {error}
            </p>
          )}

          <Button type="submit" loading={busy} className="w-full">
            Sign in
          </Button>

          <p className="text-center text-[11px] text-ink-muted">
            First time here? Use the link your clinic emailed you to choose a password.
          </p>
        </form>

        <p className="mt-5 text-center text-[11px] text-ink-muted leading-relaxed">
          Your information is private and access is logged.
        </p>
      </div>
    </div>
  );
}

/* ================================================================== *
 * Set password
 * ================================================================== */

function SetPassword() {
  const { setPassword } = usePortal();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';

  const [value, setValue] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Mirrors the server's rule. Checked here only to give immediate feedback -
  // the server enforces it regardless of what this component believes.
  const tooShort = value.length > 0 && value.length < 12;
  const mismatch = confirm.length > 0 && confirm !== value;

  async function onSubmit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await setPassword(token, value);
      navigate('/portal', { replace: true });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not set your password');
    } finally {
      setBusy(false);
    }
  }

  if (!token) {
    return (
      <div className="min-h-screen grid place-items-center px-4">
        <Card className="max-w-sm text-center">
          <p className="font-display text-sm font-semibold text-ink">This link is incomplete</p>
          <p className="mt-1.5 text-sm text-ink-muted">
            Open the link directly from the email your clinic sent you.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid place-items-center px-4 py-10">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <h1 className="font-display text-2xl font-extrabold text-ink">Choose a password</h1>
          <p className="mt-1.5 text-sm text-ink-muted">
            This is the last step before you can see your results.
          </p>
        </div>

        <form onSubmit={onSubmit} className="glass-prominent mcos-enter rounded-lg2 p-6 space-y-4">
          <Field
            label="New password"
            required
            hint="At least 12 characters."
            error={tooShort ? 'A little longer, 12 characters minimum.' : undefined}
          >
            <input
              type="password"
              autoComplete="new-password"
              required
              value={value}
              onChange={(event) => setValue(event.target.value)}
              className={inputClass}
            />
          </Field>

          <Field
            label="Confirm password"
            required
            error={mismatch ? 'These two do not match.' : undefined}
          >
            <input
              type="password"
              autoComplete="new-password"
              required
              value={confirm}
              onChange={(event) => setConfirm(event.target.value)}
              className={inputClass}
            />
          </Field>

          {error && (
            <p
              role="alert"
              className="text-sm text-red-600 bg-red-50/80 border border-red-200 rounded-control px-3 py-2"
            >
              {error}
            </p>
          )}

          <Button
            type="submit"
            loading={busy}
            disabled={value.length < 12 || confirm !== value}
            className="w-full"
          >
            Save and continue
          </Button>
        </form>
      </div>
    </div>
  );
}

/* ================================================================== *
 * Account: renewal and payment
 * ================================================================== */

function formatDate(value: string): string {
  return formatPortalDate(value);
}

function RenewalCard({ me, onDone }: { me: PortalMe; onDone: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!me.renewalDue) return null;

  async function confirm(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await portalRequest('/renewal/confirm', { method: 'POST' });
      onDone();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not confirm');
    } finally {
      setBusy(false);
    }
  }

  return (
    <PhoneCard className="px-[13px] py-[12px]">
      <CardHead label="Your programme" />
      <h2 className="mt-[8px] font-display text-[17px] font-semibold leading-tight" style={{ ...DISPLAY, color: TONE.heading }}>
        Ready for another six months?
      </h2>
      <p className="mt-[4px] text-[12.5px] leading-snug" style={{ color: TONE.body }}>
        Your current monitoring cycle is coming to an end. Confirming here tells your clinic you
        would like to continue, and they will arrange your next blood draw.
      </p>
      <Button onClick={() => void confirm()} loading={busy} size="sm" className="mt-[10px]">
        Yes, continue
      </Button>
      {error && <p className="mt-3 text-[12.5px] text-alert">{error}</p>}
    </PhoneCard>
  );
}

/**
 * Card entry, for the patient's own payment method.
 *
 * The same shape as the clinic's card form, and for the same PCI reason: the
 * number is typed into a Stripe-hosted iframe, confirmed against Stripe
 * directly by the browser, and only the resulting PaymentMethod id is ever
 * sent to MCOS. No card number reaches this application at any point.
 *
 * It calls the PORTAL client, not the clinic one, so the request carries a
 * patient token. The two sessions never share a header.
 */
function PatientCardForm({
  publishableKey,
  onSaved,
  onCancel,
}: {
  publishableKey: string;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const mountRef = useRef<HTMLDivElement>(null);
  const stripeRef = useRef<StripeJs | null>(null);
  type CardElement = ReturnType<ReturnType<StripeJs['elements']>['create']>;
  const cardRef = useRef<CardElement | null>(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let cardEl: { unmount: () => void } | null = null;
    getStripe(publishableKey)
      .then((stripe) => {
        if (cancelled || !mountRef.current) return;
        stripeRef.current = stripe;
        const el = stripe.elements().create('card', { style: { base: { fontSize: '14px' } } });
        el.mount(mountRef.current);
        el.on('change', (event) => setError(event.error?.message ?? null));
        cardRef.current = el;
        cardEl = el;
        setReady(true);
      })
      .catch(() => setError('Could not load the secure card form. Check your connection.'));
    return () => {
      cancelled = true;
      try {
        cardEl?.unmount();
      } catch {
        /* already gone */
      }
    };
  }, [publishableKey]);

  async function submit(): Promise<void> {
    const stripe = stripeRef.current;
    const card = cardRef.current;
    if (!stripe || !card) return;
    setSaving(true);
    setError(null);
    try {
      const { clientSecret } = await portalRequest<{ clientSecret: string }>(
        '/billing/setup-intent',
        { method: 'POST', body: {} },
      );
      const result = await stripe.confirmCardSetup(clientSecret, {
        payment_method: { card },
      });
      if (result.error) {
        setError(result.error.message ?? 'That card could not be saved.');
        return;
      }
      const paymentMethodId = result.setupIntent?.payment_method;
      if (!paymentMethodId) {
        setError('The payment provider did not confirm the card. Please try again.');
        return;
      }
      await portalRequest('/billing/card', { method: 'PUT', body: { paymentMethodId } });
      onSaved();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not save the card.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <div ref={mountRef} className="rounded-control border border-ink/10 bg-white px-3 py-3" />
      {error && <p className="mt-2 text-xs text-red-600">{error}</p>}
      <div className="mt-3 flex gap-2">
        <Button size="sm" onClick={() => void submit()} loading={saving} disabled={!ready}>
          Save card
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
      <p className="mt-2 text-[11px] text-ink-muted">
        Your card details go straight to our payment provider and are never stored here.
      </p>
    </div>
  );
}

function PaymentCard({ me, onChanged }: { me: PortalMe; onChanged: () => void }) {
  const [editing, setEditing] = useState(false);
  const [publishableKey, setPublishableKey] = useState<string | null>(null);
  const [unavailable, setUnavailable] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function begin(): Promise<void> {
    setBusy(true);
    setUnavailable(null);
    try {
      // This call both proves Stripe is reachable and returns the publishable
      // key the form needs, so one request does the work of two.
      const { publishableKey: key } = await portalRequest<{ publishableKey: string }>(
        '/billing/setup-intent',
        { method: 'POST', body: {} },
      );
      if (!key) {
        setUnavailable('Card payments are not set up yet. Please contact your clinic.');
        return;
      }
      setPublishableKey(key);
      setEditing(true);
    } catch (caught) {
      setUnavailable(
        caught instanceof Error ? caught.message : 'Card payments are unavailable right now.',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <PhoneCard className="px-[13px] py-[12px]">
      <CardHead label="Payment method" />
      <div className="mt-[10px]" />
      {me.card?.last4 ? (
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-sm font-semibold text-ink capitalize">
              {me.card.brand ?? 'Card'} ending {me.card.last4}
            </p>
            <p className="text-xs text-ink-muted mt-0.5">
              Expires {String(me.card.expMonth ?? '').padStart(2, '0')}/{me.card.expYear}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Pill className="bg-sage/20 text-sage-deep">Active</Pill>
            {!editing && (
              <Button size="sm" variant="secondary" loading={busy} onClick={() => void begin()}>
                Replace card
              </Button>
            )}
          </div>
        </div>
      ) : (
        !editing && (
          <div className="flex flex-wrap items-center justify-between gap-4">
            <p className="text-sm text-ink-muted">No card saved yet.</p>
            <Button size="sm" loading={busy} onClick={() => void begin()}>
              Add a card
            </Button>
          </div>
        )
      )}

      {unavailable && <p className="mt-3 text-sm text-red-600">{unavailable}</p>}

      {editing && publishableKey && (
        <div className="mt-4">
          <PatientCardForm
            publishableKey={publishableKey}
            onSaved={() => {
              setEditing(false);
              onChanged();
            }}
            onCancel={() => setEditing(false)}
          />
        </div>
      )}

      <p className="mt-4 text-[11px] leading-relaxed" style={{ color: TONE.faint }}>
        Card details are handled by our payment provider and never stored on this site.
      </p>
    </PhoneCard>
  );
}

/* ================================================================== *
 * One report, in detail
 * ================================================================== */

interface MarkerScore {
  markerId: string;
  domain: string;
  rawValue: number;
  unit: string;
  bandLabel: string;
  markerScore: number;
}

interface PortalReportDetail {
  id: string;
  releasedAt: string;
  generatedAt: string;
  comparisonType: string;
  scoreRun: {
    metabolicScore: number | null;
    inflammationScore: number | null;
    liverScore: number | null;
    confidence: string;
    engineVersion: string;
    markerScores: MarkerScore[];
  };
  labReport: { collectionDate: string };
  detail: ReportDetailSections;
}

/** Group markers by the domain they belong to, preserving server order. */
function byDomain(markers: MarkerScore[]): [string, MarkerScore[]][] {
  const groups = new Map<string, MarkerScore[]>();
  for (const marker of markers) {
    const bucket = groups.get(marker.domain);
    if (bucket) bucket.push(marker);
    else groups.set(marker.domain, [marker]);
  }
  return [...groups.entries()];
}

/**
 * Band colouring.
 *
 * These are the ENGINE's three bands and they carry the same meaning here as
 * on the printed report, deliberately not restyled to the V3 palette, because
 * a patient comparing screen to paper must not see two different colours for
 * the same result.
 */
function bandClass(label: string): string {
  const value = label.toLowerCase();
  if (value.includes('optimal') || value.includes('good')) {
    return 'bg-[var(--band-optimal)]/15 text-[var(--band-optimal)]';
  }
  if (value.includes('risk') || value.includes('poor') || value.includes('high')) {
    return 'bg-[var(--band-risk)]/15 text-[var(--band-risk)]';
  }
  return 'bg-[var(--band-warn)]/20 text-ink';
}

/* ------------------------------------------------------------------ *
 * The rest of the report
 * ------------------------------------------------------------------ */

/**
 * Everything the printed report carries beyond the scores and markers.
 *
 * The client asked for "all the data from the PDF report" to be visualised,
 * "interactive and expandable so that the amount of text is [not]
 * overwhelming". Both halves of that matter: leaving sections out makes the
 * portal a summary of a document the patient also receives in full, and
 * printing them all open makes it a wall of text nobody reads.
 *
 * So every section starts closed. Two sections the client took off the
 * patient's screens on 23 September are not drawn at all: "Remove 'What Your
 * Results Mean.' ... Remove 'How This Compares With Last Time.'" The movement
 * since the last draw is still shown, as the pill under each score ring, which
 * is how the dashboard shows it. The printed report is unchanged.
 *
 * Read from the report's stored snapshot, the same model the PDF was rendered
 * from, so the screen cannot drift from the paper.
 */

interface TherapyOption {
  therapyId: string;
  therapyName: string;
  whyConsider: string[];
  route: string | null;
}

interface ReportDetailSections {
  interpretation: string[] | null;
  closingLine: string[] | null;
  highlights: Record<string, { label?: string; value?: string; band_label?: string }[]> | null;
  trend: {
    deltas: { metabolic: number | null; inflammation: number | null; liver: number | null } | null;
  } | null;
  clinicalPatterns:
    | {
        patternId: string;
        name: string;
        priorityLabel: string;
        positive: boolean;
        patientExplanation: string;
        whyItMatters: string;
        evidence: string[];
      }[]
    | null;
  therapies: {
    peptides: TherapyOption[];
    ivim: TherapyOption[];
    disclaimer: string | null;
  } | null;
  bodyComposition: {
    enabled: boolean;
    mode: string;
    summary: string;
    current_date: string;
    baseline_date: string | null;
    comparisons: { label?: string; current?: string; baseline?: string; change?: string }[];
    anthropometrics: Record<string, unknown>;
    unavailable_metrics: string[];
  } | null;
  longevity: {
    health_age: {
      status: string;
      chronological_age: number | null;
      health_age: number | null;
      difference: number | null;
      difference_label: string | null;
      message: string | null;
    };
    domains: Record<string, number | null>;
    cellular: { score: number | null; gki: number | null; gki_status: string } | null;
  } | null;
  clinicalTwin: {
    cohortCount: number;
    confidence: string;
    windowLabel: string;
    matchingFactors: string[];
    stats: {
      weight?: { avg_change_lb: number | null };
      glycemic?: { improved_pct: number | null };
      ldl?: { improved_pct: number | null };
      continuation?: { pct: number | null };
    };
    illustrative: boolean;
    disclaimer: string;
  } | null;
  warnings: string[] | null;
}

/** Title case a snake_case domain key, for the longevity rings. */
function humanise(key: string): string {
  return key.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());
}

function ReportSections({ detail }: { detail: ReportDetailSections }) {
  const longevity = detail.longevity;
  const body = detail.bodyComposition;
  const patterns = detail.clinicalPatterns ?? [];
  const therapies = detail.therapies;
  const twin = detail.clinicalTwin;

  return (
    <>
      {longevity && (
        <Disclosure
          title="Health age"
          summary={
            longevity.health_age.health_age !== null
              ? `${longevity.health_age.health_age} years${
                  longevity.health_age.difference_label
                    ? `, ${longevity.health_age.difference_label.toLowerCase()}`
                    : ''
                }`
              : 'Not enough data from this draw'
          }
          hint="An estimate built from several blood markers together, not a measure of how long you will live. Your clinician can put it in context."
        >
          {longevity.health_age.health_age === null ? (
            <p className="text-sm text-ink-muted">
              {longevity.health_age.message ??
                'This draw did not include everything needed to estimate a health age.'}
            </p>
          ) : (
            <div className="space-y-4">
              <div className="flex flex-wrap gap-8">
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-ink-muted">Your age</p>
                  <p className="font-display text-2xl font-bold text-ink">
                    {longevity.health_age.chronological_age ?? 'n/a'}
                  </p>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-ink-muted">Health age</p>
                  <p className="font-display text-2xl font-bold text-ink">
                    {longevity.health_age.health_age}
                  </p>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-ink-muted">Difference</p>
                  <p className="font-display text-2xl font-bold text-ink">
                    {longevity.health_age.difference !== null
                      ? `${longevity.health_age.difference > 0 ? '+' : ''}${longevity.health_age.difference}`
                      : 'n/a'}
                  </p>
                </div>
              </div>

              <ul className="grid gap-1.5 sm:grid-cols-2">
                {Object.entries(longevity.domains).map(([key, value]) => (
                  <li key={key} className="flex items-baseline justify-between gap-2 text-sm">
                    <span className="text-ink-muted">{humanise(key)}</span>
                    <span className="font-display font-bold tabular-nums text-ink">
                      {value === null ? 'n/a' : Math.round(value)}
                    </span>
                  </li>
                ))}
              </ul>

              {longevity.cellular && longevity.cellular.gki !== null && (
                <p className="text-sm text-ink-muted">
                  Cellular and metabolic health: GKI {longevity.cellular.gki}
                  {longevity.cellular.gki_status
                    ? `, ${longevity.cellular.gki_status.toLowerCase().replace(/_/g, ' ')}`
                    : ''}
                  .
                </p>
              )}
            </div>
          )}
        </Disclosure>
      )}

      {body?.enabled && (
        <Disclosure
          title="Body composition"
          summary={
            body.mode === 'enhanced'
              ? 'From your body scan'
              : 'From your height and weight'
          }
          hint="Enhanced measures come from an InBody scan. Without one, this section is built from height and weight alone, and the scan-only measures are listed as unavailable rather than estimated."
        >
          <div className="space-y-4">
            {body.summary && <p className="text-sm leading-relaxed text-ink">{body.summary}</p>}

            {body.comparisons.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[11px] uppercase tracking-wide text-ink-muted">
                      <th className="pb-1.5 font-semibold">Measure</th>
                      <th className="pb-1.5 pl-3 font-semibold">Baseline</th>
                      <th className="pb-1.5 pl-3 font-semibold">Now</th>
                      <th className="pb-1.5 pl-3 text-right font-semibold">Change</th>
                    </tr>
                  </thead>
                  <tbody>
                    {body.comparisons.map((row, index) => (
                      <tr key={index} className="border-t border-ink/5">
                        <td className="py-2 pr-3 text-ink">{row.label ?? 'Measure'}</td>
                        <td className="py-2 pl-3 text-ink-muted">{row.baseline ?? 'n/a'}</td>
                        <td className="py-2 pl-3 text-ink">{row.current ?? 'n/a'}</td>
                        <td className="py-2 pl-3 text-right text-ink-muted">
                          {row.change ?? 'n/a'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/*
              Named rather than hidden. A patient who has read about a measure
              and cannot find it should be told it was not collected, not left
              to assume the result was bad.
            */}
            {body.unavailable_metrics.length > 0 && (
              <p className="text-[11px] text-ink-muted">
                Not measured on this draw: {body.unavailable_metrics.join(', ')}. These need a
                body scan.
              </p>
            )}
          </div>
        </Disclosure>
      )}

      {patterns.length > 0 && (
        <Disclosure
          title="Patterns your clinician is watching"
          summary={`${patterns.length} identified from this draw`}
          hint="Combinations of markers that tend to travel together. They are observations for your clinician to interpret, not diagnoses."
        >
          <ul className="space-y-4">
            {patterns.map((pattern) => (
              <li key={pattern.patternId}>
                <p className="flex flex-wrap items-center gap-2 text-sm font-semibold text-ink">
                  {pattern.name}
                  <Pill
                    className={
                      pattern.positive
                        ? 'bg-sage/15 text-sage-deep'
                        : 'bg-ink/5 text-ink-muted'
                    }
                  >
                    {pattern.positive ? 'Strength' : pattern.priorityLabel}
                  </Pill>
                </p>
                {pattern.patientExplanation && (
                  <p className="mt-1 text-sm leading-relaxed text-ink-muted">
                    {pattern.patientExplanation}
                  </p>
                )}
                {pattern.whyItMatters && (
                  <p className="mt-1 text-sm leading-relaxed text-ink-muted">
                    {pattern.whyItMatters}
                  </p>
                )}
                {pattern.evidence?.length > 0 && (
                  <p className="mt-1 text-[11px] text-ink-muted">
                    Based on {pattern.evidence.join(', ')}
                  </p>
                )}
              </li>
            ))}
          </ul>
        </Disclosure>
      )}

      {therapies && (therapies.peptides.length > 0 || therapies.ivim.length > 0) && (
        <Disclosure
          title="Options to discuss with your clinician"
          summary={`${therapies.peptides.length + therapies.ivim.length} suggested by your results`}
          hint="Drawn from your own results and goals as a starting point for a conversation. Nothing here has been prescribed, and only your clinician can decide what is right for you."
        >
          <div className="space-y-5">
            {([
              ['peptides', 'Peptides'],
              ['ivim', 'IV and IM'],
            ] as const).map(([group, label]) =>
              therapies[group].length > 0 ? (
                <div key={group}>
                  <p className="mb-2 text-[11px] uppercase tracking-wide text-ink-muted">
                    {label}
                  </p>
                  <ul className="space-y-3">
                    {therapies[group].map((item) => (
                      <li key={item.therapyId}>
                        <p className="flex flex-wrap items-center gap-2 text-sm font-semibold text-ink">
                          {item.therapyName}
                          {item.route && (
                            <Pill className="bg-ink/5 text-ink-muted">{item.route}</Pill>
                          )}
                        </p>
                        {/*
                          The patient-specific reasons, drawn from their own
                          goals, labs and patterns. This is the part that makes
                          a suggestion theirs rather than a catalogue entry.
                        */}
                        {item.whyConsider?.length > 0 && (
                          <ul className="mt-1 space-y-0.5">
                            {item.whyConsider.map((reason, index) => (
                              <li key={index} className="text-sm text-ink-muted">
                                {reason}
                              </li>
                            ))}
                          </ul>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null,
            )}

            {/* Required by the report spec whenever anything is listed. */}
            {therapies.disclaimer && (
              <p className="rounded-sm2 bg-ink/[0.04] px-3 py-2 text-[11px] leading-relaxed text-ink-muted">
                {therapies.disclaimer}
              </p>
            )}
          </div>
        </Disclosure>
      )}

      {twin && (
        <Disclosure
          title="Patients like you"
          summary={`${twin.cohortCount} people with a similar profile, over ${twin.windowLabel}`}
          hint="How a matched group of patients progressed. It describes what happened to them, not a prediction about you."
        >
          <div className="space-y-4">
            {twin.matchingFactors?.length > 0 && (
              <p className="text-sm text-ink-muted">
                Matched on {twin.matchingFactors.join(', ')}.
              </p>
            )}

            <ul className="grid gap-2 sm:grid-cols-2">
              {[
                {
                  label: 'Average weight change',
                  value:
                    twin.stats.weight?.avg_change_lb !== null &&
                    twin.stats.weight?.avg_change_lb !== undefined
                      ? `${twin.stats.weight.avg_change_lb} lb`
                      : null,
                },
                {
                  label: 'Blood sugar improved',
                  value:
                    twin.stats.glycemic?.improved_pct !== null &&
                    twin.stats.glycemic?.improved_pct !== undefined
                      ? `${twin.stats.glycemic.improved_pct}%`
                      : null,
                },
                {
                  label: 'Cholesterol improved',
                  value:
                    twin.stats.ldl?.improved_pct !== null &&
                    twin.stats.ldl?.improved_pct !== undefined
                      ? `${twin.stats.ldl.improved_pct}%`
                      : null,
                },
                {
                  label: 'Still on programme',
                  value:
                    twin.stats.continuation?.pct !== null &&
                    twin.stats.continuation?.pct !== undefined
                      ? `${twin.stats.continuation.pct}%`
                      : null,
                },
              ].map((stat) => (
                <li key={stat.label} className="flex items-baseline justify-between gap-2 text-sm">
                  <span className="text-ink-muted">{stat.label}</span>
                  <span className="font-display font-bold text-ink">{stat.value ?? 'n/a'}</span>
                </li>
              ))}
            </ul>

            <p className="rounded-sm2 bg-ink/[0.04] px-3 py-2 text-[11px] leading-relaxed text-ink-muted">
              {twin.illustrative
                ? 'This comparison uses a representative group while the live network is being built. '
                : ''}
              {twin.disclaimer}
            </p>
          </div>
        </Disclosure>
      )}
    </>
  );
}

function PortalReportDetail() {
  const { id } = useParams<{ id: string }>();
  const { me } = usePortal();
  const navigate = useNavigate();
  const [report, setReport] = useState<PortalReportDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    window.scrollTo(0, 0);
    void (async () => {
      try {
        const data = await portalRequest<{ report: PortalReportDetail }>(`/reports/${id}`);
        setReport(data.report);
      } catch {
        /*
         * A withheld report and a non-existent one are the same 404 from here,
         * and the message says so without implying a result is being kept from
         * them, a patient chasing their clinic over a report nobody has
         * reviewed helps no one.
         */
        setError('That report is not available.');
      }
    })();
  }, [id]);

  if (!me) return null;

  const deltas = report?.detail?.trend?.deltas ?? null;
  const rings = report
    ? ([
        { key: 'metabolic', score: report.scoreRun.metabolicScore, movement: deltas?.metabolic ?? null },
        { key: 'inflammation', score: report.scoreRun.inflammationScore, movement: deltas?.inflammation ?? null },
        { key: 'liver', score: report.scoreRun.liverScore, movement: deltas?.liver ?? null },
      ] as const)
    : [];
  const ringLabel = { metabolic: 'Metabolism', inflammation: 'Inflammation', liver: 'Liver Health' } as const;

  /*
   * The report keeps its own chrome and sits OUTSIDE the tab bar: it is a long
   * read with its own way back, drawn in the same phone system as the tabs.
   */
  return (
    <div className="min-h-screen bg-paper" style={{ color: TONE.heading }}>
      <div className="mx-auto w-full max-w-[430px]">
        <header className="flex items-center gap-3 px-4 pb-2 pt-[max(12px,env(safe-area-inset-top))]">
          <button
            type="button"
            onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/portal'))}
            aria-label="Back"
            className="-ml-1 grid h-9 w-9 shrink-0 place-items-center rounded-full"
            style={{ color: TONE.forestText }}
          >
            <ArrowLeft size={22} />
          </button>
          <Link to="/portal" className="min-w-0 flex-1" aria-label="MCOS, go to your dashboard">
            <span
              className="block font-display text-[25px] font-semibold leading-none"
              style={{ ...DISPLAY, color: TONE.heading }}
            >
              MCOS
            </span>
            <span
              className="mt-[5px] block whitespace-pre text-[7px] font-medium uppercase leading-none"
              style={{ color: TONE.body, letterSpacing: '0.11em' }}
            >
              {'Metabolic health.  Real progress.'}
            </span>
          </Link>
        </header>

        <main className="space-y-[8px] px-3 pb-10">
          <PageTitle
            title="Your Report"
            subtitle={
              report
                ? `From your blood draw on ${formatDate(report.labReport.collectionDate)}.`
                : 'Your results, reviewed and released by your clinician.'
            }
          />

          {error && (
            <PhoneCard className="px-4 py-6">
              <EmptyState
                title="That report is not available"
                body="It may still be with your clinician. Your dashboard shows everything ready for you."
              />
            </PhoneCard>
          )}

          {!error && !report && (
            <PhoneCard className="px-4 py-4">
              <LoadingRows rows={4} />
            </PhoneCard>
          )}

          {report && (
            <>
              {/* The three scores, as on the dashboard. No overall score:
                  Brian, 23 September, "Remove the Overall Score completely". */}
              <PhoneCard className="px-[13px] pb-[12px] pt-[13px]">
                <CardHead label="Your clinical picture" />
                <div className="mt-[18px] grid grid-cols-3">
                  {rings.map((ring) => (
                    <div key={ring.key} className="flex flex-col items-center">
                      <ScoreRing ringKey={ring.key} score={ring.score} />
                      <p className="mt-[7px] text-[12.5px] font-medium leading-tight" style={{ color: TONE.heading }}>
                        {ringLabel[ring.key]}
                      </p>
                      <span className="mt-[8px]">
                        <MovePill ringKey={ring.key} movement={ring.movement} />
                      </span>
                    </div>
                  ))}
                </div>
                <p className="mt-[12px] text-[11px] leading-snug" style={{ color: TONE.faint }}>
                  Reviewed and released by your clinician on {formatDate(report.releasedAt)}.
                  {deltas ? ' Movement is since your previous blood draw.' : ''}
                </p>
              </PhoneCard>

              {report.detail && <ReportSections detail={report.detail} />}

              {report.scoreRun.markerScores.length > 0 ? (
                byDomain(report.scoreRun.markerScores).map(([domain, markers]) => (
                  <Disclosure
                    key={domain}
                    title={domain}
                    summary={`${markers.length} ${markers.length === 1 ? 'marker' : 'markers'}`}
                    hint="Each marker's value from your blood draw, with the band it falls into. The bands match your printed report exactly."
                  >
                    <ul className="-mx-5 -my-4 divide-y divide-ink/5">
                      {markers.map((marker) => (
                        <li
                          key={marker.markerId}
                          className="px-5 py-3 flex flex-wrap items-center justify-between gap-3"
                        >
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-ink">{marker.markerId}</p>
                            <p className="text-xs text-ink-muted mt-0.5">
                              {marker.rawValue} {marker.unit}
                            </p>
                          </div>
                          <Pill className={bandClass(marker.bandLabel)}>{marker.bandLabel}</Pill>
                        </li>
                      ))}
                    </ul>
                  </Disclosure>
                ))
              ) : (
                <PhoneCard className="px-4 py-6">
                  <EmptyState
                    title="No individual markers to show"
                    body="Your scores above are based on this blood draw."
                  />
                </PhoneCard>
              )}

              <p className="px-1 text-[11px] leading-relaxed" style={{ color: TONE.faint }}>
                These results are part of your care with your clinic. If anything here worries you,
                contact them directly. This page is not a substitute for medical advice.
              </p>
            </>
          )}
        </main>
      </div>
    </div>
  );
}

/* ================================================================== *
 * Routing
 * ================================================================== */

/**
 * "Account": the money and the cycle, kept off the health screens.
 *
 * A card expiry notice has no business sharing a screen with somebody's A1c.
 * Both are theirs, only one is something they came to read, and mixing them is
 * how a health app starts feeling like a billing portal.
 *
 * The care team section names the clinic and the clinician and opens Message
 * Care Team (S-10), with the alert switches (MS-010, N-03) once alerts exist.
 */
interface CareTeam {
  clinic: { name: string } | null;
  clinician: { name: string } | null;
}

function PortalAccount() {
  const { me, reload, logout } = usePortal();
  const location = useLocation();
  const careTeam = usePortalQuery<CareTeam>('care-team', '/care-team');

  useEffect(() => {
    if (location.hash !== '#care-team') return;
    document.getElementById('care-team')?.scrollIntoView({ block: 'start' });
  }, [location.hash, careTeam.data]);

  if (!me) return null;

  const clinic = careTeam.data?.clinic?.name ?? null;
  const rows: Array<[string, string]> = me.subscription
    ? [
        ['Started', formatDate(me.subscription.enrolledAt)],
        ['Length', `${me.subscription.cycleMonths} months`],
        ['Payments made', `${me.subscription.installmentsPaid} of ${me.subscription.cycleMonths}`],
      ]
    : [];

  return (
    <div className="space-y-[8px] pb-1">
      <PageTitle
        title="Account"
        subtitle={`${me.patient.firstName} ${me.patient.lastName}${me.patient.email ? `, ${me.patient.email}` : ''}`}
      />

      <RenewalCard me={me} onDone={() => void reload()} />

      <section id="care-team" className="scroll-mt-3">
        <PhoneCard className="px-[13px] py-[12px]">
          <CardHead label="Your care team" />
          <p
            className="mt-[8px] font-display text-[18px] font-semibold leading-tight"
            style={{ ...DISPLAY, color: TONE.heading }}
          >
            {clinic ?? 'Your clinic'}
          </p>
          {careTeam.data?.clinician && (
            <p className="mt-[3px] text-[12.5px]" style={{ color: TONE.body }}>
              Your clinician: {careTeam.data.clinician.name}
            </p>
          )}
          <Link
            to="/portal/messages"
            className="mt-[10px] flex h-[38px] items-center justify-center gap-2 rounded-[8px] text-[13px] font-semibold text-white"
            style={{ background: 'var(--forest)' }}
          >
            Message your care team
            <ArrowRight size={14} strokeWidth={2} />
          </Link>
          <AlertSwitches />
        </PhoneCard>
      </section>

      <PaymentCard me={me} onChanged={() => void reload()} />

      {rows.length > 0 && (
        <PhoneCard className="px-[13px] py-[12px]">
          <CardHead label="Your programme" />
          <dl className="mt-[8px] divide-y text-[13px]" style={{ borderColor: '#ecebe7' }}>
            {rows.map(([label, value]) => (
              <div key={label} className="flex justify-between gap-4 py-[7px]" style={{ borderColor: '#ecebe7' }}>
                <dt style={{ color: TONE.body }}>{label}</dt>
                <dd className="font-semibold" style={{ color: TONE.heading }}>
                  {value}
                </dd>
              </div>
            ))}
          </dl>
        </PhoneCard>
      )}

      <button
        type="button"
        onClick={() => void logout()}
        className="flex h-[40px] w-full items-center justify-center gap-2 rounded-[8px] border bg-white text-[13px] font-semibold transition-colors hover:bg-mint"
        style={{ borderColor: '#b9cfca', color: TONE.forestText }}
      >
        Sign out
        <ArrowRight size={14} strokeWidth={2} />
      </button>
    </div>
  );
}

interface AlertPrefs {
  available: boolean;
  smsAlerts: boolean;
  hasPhone: boolean;
  consented: boolean;
  optedOut: boolean;
  emailAvailable: boolean;
  emailAlerts: boolean;
  hasEmail: boolean;
  emailOptedOut: boolean;
}

/**
 * MS-010 and N-03: "Text me" and "Email me when my care team replies". Each
 * shows only once that kind of alert is switched on for MCOS; a switch for an
 * alert that cannot be sent would be a promise.
 */
function AlertSwitches() {
  const prefs = usePortalQuery<AlertPrefs>('alert-prefs', '/alerts');
  const [saved, setSaved] = useState<AlertPrefs | null>(null);
  const [saving, setSaving] = useState(false);
  const p = saved ?? prefs.data;
  if (!p || (!p.available && !p.emailAvailable)) return null;
  const save = async (change: { smsAlerts?: boolean; emailAlerts?: boolean }) => {
    setSaving(true);
    try {
      setSaved(await portalRequest<AlertPrefs>('/alerts', { method: 'PUT', body: change }));
    } finally {
      setSaving(false);
    }
  };
  const textBlocked = !p.hasPhone ? 'Add a mobile number with your clinic to get text alerts.' : !p.consented ? 'Your clinic needs your OK to text you before alerts can start.' : p.optedOut ? 'You replied STOP, so texts are off. Reply START to turn them back on.' : null;
  const emailBlocked = !p.hasEmail ? 'Add an email address with your clinic to get email alerts.' : p.emailOptedOut ? 'You unsubscribed from email, so email alerts are off.' : null;
  const row = (label: string, on: boolean, blocked: string | null, change: () => void, testId: string) => (
    <label className="flex items-center justify-between gap-3 py-[6px] text-[13px]" style={{ color: blocked ? TONE.body : TONE.heading }}>
      <span>{label}</span>
      <input type="checkbox" role="switch" checked={on && !blocked} disabled={saving || Boolean(blocked)} onChange={change} className="h-4 w-4 accent-[var(--forest)]" data-testid={testId} />
    </label>
  );
  const notes = [p.available ? textBlocked : null, p.emailAvailable ? emailBlocked : null].filter(Boolean);
  return (
    <div className="mt-[10px] border-t pt-[4px]" style={{ borderColor: '#ecebe7' }}>
      {p.available && row('Text me when my care team replies', p.smsAlerts, textBlocked, () => void save({ smsAlerts: !p.smsAlerts }), 'text-alert-switch')}
      {p.emailAvailable && row('Email me when my care team replies', p.emailAlerts, emailBlocked, () => void save({ emailAlerts: !p.emailAlerts }), 'email-alert-switch')}
      <p className="mt-[2px] text-[11.5px] leading-snug" style={{ color: TONE.body }}>
        {notes.length ? notes.join(' ') : 'An alert only says there is a message. Read it here in the app.'}
      </p>
    </div>
  );
}

function PortalGate() {
  const { status } = usePortal();

  if (status === 'loading') {
    return (
      <div className="min-h-screen grid place-items-center">
        <span className="text-ink-muted">
          <Spinner className="h-6 w-6" />
        </span>
      </div>
    );
  }

  return (
    <Routes>
      <Route path="set-password" element={<SetPassword />} />
      {status === 'authenticated' ? (
        <>
          {/*
            The report page keeps its own chrome and sits OUTSIDE the tab bar.
            It is a long read with its own back link, and a fixed tab bar under
            a twelve-section document is a strip of navigation the reader has
            to scroll past on every screen.
          */}
          <Route path="reports/:id" element={<PortalReportDetail />} />
          {/* Message Care Team (S-10, S-11): full screens, like board 8. */}
          <Route path="messages" element={<PortalMessages />} />
          <Route path="messages/new" element={<PortalNewMessage />} />
          <Route path="messages/:id" element={<PortalConversation />} />
          <Route element={<PortalShell />}>
            <Route index element={<PortalHome />} />
            <Route path="health" element={<PortalTwin />} />
            <Route path="plan" element={<PortalPlan />} />
            <Route path="progress" element={<PortalProgress />} />
            <Route path="resources" element={<PortalResources />} />
            <Route path="scan" element={<PortalBodyScan />} />
            <Route path="dose" element={<PortalDoseSteps />} />
            <Route path="account" element={<PortalAccount />} />
            {/* Anything unrecognised goes home rather than showing a dead
                screen with a tab bar under it. */}
            <Route path="*" element={<PortalHome />} />
          </Route>
        </>
      ) : (
        <Route path="*" element={<PortalLogin />} />
      )}
    </Routes>
  );
}

/** Mounted at `/portal/*`, entirely outside the clinic app's auth provider. */
export function PortalApp() {
  return (
    <PortalProvider>
      <PortalGate />
    </PortalProvider>
  );
}
